import { ApplicationService, DataQualityService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ApplicationBulkUpdateParams, DeserializedApplication, SchemaProperty, UOMS } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Alert, SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { DataQualityEnabledTypes } from '../../common/Constants';
import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes, Query } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { DEFAULT_DATABASE_UOM, uomConversion } from '../../common/uomConversion';
import { deepCopy, format, generatePluralStr } from '../../common/util';
import DataQualityRuleValidation from '../data-quality/DataQualityRuleValidation';
import AttributeActionGroup from './common/AttributesActionGroup';
import PortfolioTableActionGroup from './common/PortfolioTableActionGroup';
import { APPLICATION_ID_COLUMN, ColumnOrderType, EXCLUDED_APPLICATION_COLUMNS, FILTER_TOKEN_DISPLAY_LIMIT, OVERSIZED_RESOURCE_THRESHOLDS } from './constants';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './inline-editing/InlineEditContext';
import { SchemaPropertyMap } from './types';
import { generateColumnDefs, getFilterQuery } from './utils';

type ApplicationProps = FlashContextInterface;

const applicationService = new ApplicationService();
const schemaService = new SchemaService();
const dataQualityService = new DataQualityService();
const resourceName = 'applications';
const dataQualityResourceName = 'application';

const Application: FunctionComponent<ApplicationProps> = ({flash}) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId;
    const [ applicationItems, setApplicationItems ] = useState<DeserializedApplication[]>([]);
    const [ applicationSchemas, setApplicationSchemas ] = useState<SchemaProperty[]>([]);
    const [ applicationSchemaMap, setApplicationSchemaMap ] = useState<SchemaPropertyMap>({});
    const [ applicationColumnDefinitions, setApplicationColumnDefinitions ] = useState<ColumnDefinition<DeserializedApplication>[]>([]);
    const [ selectedApplications, setSelectedApplications ] = useState<DeserializedApplication[]>([]);
    const [ filteredApplications, setFilteredApplications ] = useState<DeserializedApplication[] | null>(null);
    const [ loading, setLoading ] = useState(true);
    const [ reloadRulesImmediately, setReloadRulesImmediately ] = useState(true);
    const [ defaultQuery, setDefaultQuery ] = useState<Query>();
    const { updateInlineEditFn } = useContext(InlineEditContext);
    const [ oversizedAlert, setOversizedAlert ] = useState('');
    
    const isMounted = useIsMounted();

    const loadApplicationData = async () => {
        setLoading(true);
        setSelectedApplications([]);
        try {
            if (portfolioId) {
                // Load portfolio application
                const applications = await applicationService.list(portfolioId) as DeserializedApplication[];
                if (!isMounted()) {
                    return;
                }
                if (applications.length > OVERSIZED_RESOURCE_THRESHOLDS.application) {
                    setOversizedAlert(format(constants.OVERSIZED_PORTFOLIO, [applications.length, resourceName, OVERSIZED_RESOURCE_THRESHOLDS.application]));
                } else {
                    setOversizedAlert('');
                }
                setApplicationItems(applications);

                // Load application schemas
                const appSchemas = await schemaService.listAttributes(resourceName, portfolioId);
                if (!isMounted()) {
                    return;
                }
                setApplicationSchemas(appSchemas);
                const applicationHeaders: SchemaPropertyMap = {};
                appSchemas.forEach(appSchema => {
                    applicationHeaders[appSchema.value] = {
                        ...appSchema,
                        onClickRedirect: (item?: any) => {
                            return `/portfolio/${portfolioId}/management/application-detail/${item.applicationId}/overview`;
                        },
                    };
                });
                setApplicationSchemaMap(applicationHeaders);

                // Build column definitions
                const columnDefinitions = generateColumnDefs(applications, applicationHeaders, 
                    ColumnOrderType.APPLICATION, EXCLUDED_APPLICATION_COLUMNS, true);
                // Add mapped columns
                columnDefinitions.push({
                    id: 'applicationMapped',
                    header: constants.SERVER_MAPPED_COLUMN_HEADER,
                    type: ColumnTypes.TEXT,
                    data: (item: DeserializedApplication) => item.serverMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                    cell: (item: DeserializedApplication) => item.serverMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                });
                columnDefinitions.push({
                    id: 'serverMapped',
                    header: constants.DATABASE_MAPPED_COLUMN_HEADER,
                    type: ColumnTypes.TEXT,
                    data: (item: DeserializedApplication) => item.databaseMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                    cell: (item: DeserializedApplication) => item.databaseMappings ? constants.RESOURCE_MAPPED : constants.RESOURCE_UNMAPPED,
                });
                setApplicationColumnDefinitions(columnDefinitions);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.APPLICATION_ERROR_LOADING);
                setApplicationItems([]);
                setApplicationColumnDefinitions([]);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };
    useEffect(() => {
        loadApplicationData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId ]);

    const inlineEditFunction = async (rowItem: InlineEditRowItem, columnId: string, updatedValue: InlineEditValue) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }

        const conditions: ApplicationBulkUpdateParams = {
            fieldName: columnId,
            fieldValue: updatedValue,
            portfolioId,
            applicationIds: [(rowItem as DeserializedApplication).applicationId],
        };

        try {
            await applicationService.update(conditions);
            const updateSuccessMsg = generatePluralStr(1, constants.APPLICATION_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APPLICATION_EDITING_ERROR_HEADING);
        } finally {
            await loadApplicationData();
        }
    };

    useEffect(() => {
        updateInlineEditFn(inlineEditFunction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Delete multiple applications. If applications are selected, they will be deleted. If there are no selected applications,
     * then all filtered applications will be deleted.
     * @returns void
     */
    const onConfirmBulkDeletion = async () => {
        setLoading(true);
        setReloadRulesImmediately(false);
        if (portfolioId) {
            const applicationIds: string[] = (selectedApplications.length || !filteredApplications ? selectedApplications : filteredApplications)
                .map(selectedApp => selectedApp.applicationId);
            const conditions = {
                applicationIds,
                portfolioId,
            };
            await applicationService.deleteApplication(conditions); 
        }
        await loadApplicationData();
    };

    const onConfirmDeleteAll = async () => {
        setLoading(true);
        setReloadRulesImmediately(true);
        if (portfolioId) {
            await applicationService.clear(portfolioId); 
        }
        await loadApplicationData();
    };

    const onConfirmBulkEdit = async (
        selectAll: boolean,
        selectedHeader: string,
        updatedValue: string | number | boolean,
        updatedUOM?: UOMS,
    ) => {
        setLoading(true);
        setReloadRulesImmediately(false);
        if (portfolioId) {
            const selectedSchema = applicationSchemaMap[selectedHeader];
            if (!selectedHeader || !selectedSchema) {
                flash.alert(FlashType.error, constants.BULK_UPDATE_ERROR_CONTENT, constants.APPLICATION_EDITING_ERROR_HEADING);
                return;
            }
            const selectedAppIds = (selectAll ? (filteredApplications ?? applicationItems) : selectedApplications)
                .map(selectedApp => selectedApp.applicationId);
    
            let bulkUpdateValue = updatedValue;
            if (selectedSchema.UOM && updatedUOM) {
                const dbUOM = DEFAULT_DATABASE_UOM[selectedSchema.UOM];
                bulkUpdateValue = uomConversion(Number(updatedValue), updatedUOM, dbUOM);
            }

            try {
                await applicationService.updateByAttribute(selectedAppIds, selectedHeader, bulkUpdateValue, portfolioId);
                const updateSuccessMsg = generatePluralStr(selectedAppIds.length || applicationItems.length, constants.APPLICATION_RESOURCE_NAME, '', true)
                    + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
                flash.alert(FlashType.success, updateSuccessMsg, '');
            } catch (err) {
                flash.alert(FlashType.error, (err as Error).message, constants.APPLICATION_EDITING_ERROR_HEADING);
            } finally {
                await loadApplicationData();
            }
        }
    };

    const onConfirmAddAttribute = async (attributeName: string, attributeType: string, dataType: string): Promise<void> => {
        setLoading(true);
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        const body = {
            attributeName,
            attributeType,
            dataType,
            portfolioId,
        };
        try {
            await schemaService.createAttribute(resourceName, body);
            flash.alert(FlashType.success, constants.ADD_ATTRIBUTE_SUCCESS, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ADD_ATTRIBUTE_FAILED);
        } finally {
            await loadApplicationData();
        }
    };

    const onConfirmDeleteAttribute = async (attributeName?: string): Promise<void> => {
        if (!attributeName) {
            flash.alert(FlashType.error, constants.DELETE_ATTRIBUTE_EMPTY_SCHEMA_ERROR, '');
            return;
        }
        setLoading(true);
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        try {
            await schemaService.deleteAttribute(resourceName, portfolioId, attributeName);
            flash.alert(FlashType.success, constants.DELETE_ATTRIBUTE_SUCCESS, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.DELETE_ATTRIBUTE_FAILED);
        } finally {
            await loadApplicationData();
        }
    };

    const defaultSortingState: SortingState<DeserializedApplication> = {
        isDescending: false,
        sortingColumn: {
            sortingField: APPLICATION_ID_COLUMN,
        },
    };

    const onFilterByRuleChange = async (ruleId: string) => {
        if (!portfolioId) {
            return;
        }
        try {
            const invalidApplicationIds = await dataQualityService.getInvalidRecords(portfolioId, dataQualityResourceName, ruleId);
            const query = getFilterQuery(invalidApplicationIds, APPLICATION_ID_COLUMN);
            setDefaultQuery(query);
        } catch (err) {
            flash.alert(FlashType.error, constants.RULE_FILTER_ERROR_MSG, '');
        }
    };

    const filterChanged = (applications: readonly DeserializedApplication[] | null) => {
        setFilteredApplications(deepCopy(applications));
    };

    return (
        <SpaceBetween size='m'>
            {oversizedAlert ? 
                <Alert type='info' header={constants.OVERSIZED_HEADER}>{oversizedAlert}</Alert>
                : (
                    <DataQualityRuleValidation 
                        assetType={DataQualityEnabledTypes.APPLICATION}
                        assetItems={applicationItems}
                        reloadImmediately={reloadRulesImmediately}
                        filterOnRule={onFilterByRuleChange}
                    />
                )
            }
            <AWSCATTable
                resourceName={constants.APPLICATION_TABLE_HEADER}
                resourceType='application'
                items={applicationItems}
                importWhenEmpty={true}
                columnDefinitions={applicationColumnDefinitions}
                loading={loading}
                resizeable={true}
                autoGenerateVisibleContent={true}
                onSelectionChange={setSelectedApplications as () => void}
                onFilterChange={filterChanged}
                headerActions={
                    <PortfolioTableActionGroup
                        resourceType='application'
                        resourceName={constants.APPLICATION_RESOURCE_NAME}
                        resourceCount={applicationItems.length}
                        selectionCount={selectedApplications.length}
                        hasFilter={!!filteredApplications}
                        filteredCount={filteredApplications?.length ?? 0}
                        loading={loading}
                        onConfirmDeletion={onConfirmBulkDeletion}
                        onConfirmDeleteAll={onConfirmDeleteAll}
                        onConfirmBulkEdit={onConfirmBulkEdit}
                        schemaProperties={applicationSchemas}
                        schemaPropertyMap={applicationSchemaMap}
                        attributeActions={
                            <AttributeActionGroup
                                loading={loading}
                                existingSchemas={applicationSchemas}
                                onConfirmAddAttribute={onConfirmAddAttribute}
                                onConfirmDeleteAttribute={onConfirmDeleteAttribute}
                            />
                        }
                    />
                }
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                useDefaultDownload
                defaultQuery={defaultQuery}
                filterTokenDisplayLimit={FILTER_TOKEN_DISPLAY_LIMIT}
            />
        </SpaceBetween>
    );
};

export default withFlashContext(Application);
